<template>
  <LogoutModal />
  <header
    :class="[
      'header-area bg-white text-center text-md-start pt-15 pb-15 ps-15 pe-15 ps-md-20 pe-md-20 pe-lg-30 transition mb-25 position-fixed radius-7',
      { sticky: isSticky },
    ]"
    id="header"
  >
    <div class="row align-items-center flex-row justify-content-between custom-mobile">
      <div class="col-auto">
        <div class="header-left-side justify-content-center justify-content-md-start d-flex align-items-center">
          <button
            class="header-burger-menu transition position-relative lh-1 bg-transparent p-0 border-0"
            id="header-burger-menu"
            aria-label="Abrir ou fechar menu"
            @click="stateStoreInstance.onChange"
          >
            <i class="flaticon-menu-3"></i>
          </button>
        </div>
      </div>

      <div class="col-auto area-buttons-header">
        <div class="header-right-side d-flex align-items-center justify-content-end">
          <LightDarkSwtichBtn />

          <div class="dropdown notification-dropdown security-notification-dropdown">
            <button
              type="button"
              class="security-notification-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label="Notificações de alertas da IA"
              title="Alertas da IA"
            >
              <i class="ph ph-bell"></i>
              <span v-if="recentSecurityAlerts.length" class="security-notification-badge">
                {{ recentSecurityAlerts.length }}
              </span>
            </button>

            <div class="dropdown-menu security-notification-menu bg-white border-0 radius-7">
              <div class="security-notification-header">
                <div>
                  <strong>Alertas da IA</strong>
                  <span>Últimas detecções</span>
                </div>
                <span v-if="notificationsLoading" class="security-notification-loading">Carregando...</span>
              </div>

              <ul v-if="recentSecurityAlerts.length" class="security-notification-list list-unstyled mb-0">
                <li v-for="alert in recentSecurityAlerts" :key="alert.alert_id">
                  <button
                    type="button"
                    class="security-notification-item"
                    @click="navigateToSecurityAlert(alert)"
                  >
                    <span class="security-notification-image">
                      <img
                        v-if="hasSnapshot(alert)"
                        :src="alert.snapshot_url"
                        :alt="`Detecção na câmera ${alert.camera_name || 'sem nome'}`"
                        @error="markSnapshotUnavailable(alert)"
                      />
                      <i v-else class="ph ph-camera-slash"></i>
                    </span>
                    <span class="security-notification-content">
                      <strong>{{ alert.camera_name || 'Câmera' }}</strong>
                      <span>Pessoa detectada</span>
                      <small>
                        {{ formatSecurityAlertDate(alert.detected_at) }}
                        <template v-if="alert.confidence">
                          · {{ formatConfidence(alert.confidence) }}
                        </template>
                      </small>
                    </span>
                    <i class="ph ph-caret-right security-notification-arrow"></i>
                  </button>
                </li>
              </ul>

              <div v-else-if="!notificationsLoading" class="security-notification-empty">
                <i class="ph ph-bell-slash"></i>
                <span>Nenhum alerta disponível.</span>
              </div>
            </div>
          </div>

          <div class="profile-dropdown d-flex justify-content-end">
            <button
              class="text-end fs-14 text-black-emphasis d-flex align-items-center p-0 position-relative bg-transparent border-0 transition lh-1"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span class="title d-lg-block">
                <span class="d-block fw-bold">{{ getConfigurations.name }}</span>
                <span class="text-body-emphasis fw-semibold fs-13">Config</span>
              </span>
            </button>

            <div class="dropdown dropdown-menu rounded-0 bg-white border-0 end-0 ps-10 radius-7">
              <ul class="ps-0 mb-0 list-unstyled dropdown-body">
                <li
                  class="pointer text-body-secondary fw-semibold transition position-relative"
                  data-bs-toggle="modal"
                  data-bs-target="#verticallyCenteredModal"
                >
                  <i class="flaticon-logout"></i>
                  Deslogar
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>

  <transition name="security-alert-pop">
    <aside
      v-if="activeSecurityAlert && $route.path !== '/realtime'"
      class="global-security-alert"
      role="button"
      tabindex="0"
      title="Abrir esta câmera no mosaico"
      @click="navigateToSecurityAlert(activeSecurityAlert)"
      @keydown.enter.prevent="navigateToSecurityAlert(activeSecurityAlert)"
      @keydown.space.prevent="navigateToSecurityAlert(activeSecurityAlert)"
    >
      <button
        type="button"
        class="global-security-alert-close"
        aria-label="Fechar alerta"
        @click.stop="dismissActiveSecurityAlert"
      >
        <i class="ph ph-x"></i>
      </button>
      <div class="global-security-alert-kicker">Alerta IA ao vivo</div>
      <div class="global-security-alert-title">Pessoa detectada</div>
      <div class="global-security-alert-frame">
        <img
          v-if="hasSnapshot(activeSecurityAlert)"
          :src="activeSecurityAlert.snapshot_url"
          :alt="`Detecção na câmera ${activeSecurityAlert.camera_name || 'sem nome'}`"
          @error="markSnapshotUnavailable(activeSecurityAlert)"
        />
        <div v-else class="global-security-alert-frame-empty">
          <i class="ph ph-camera-slash"></i>
          Imagem indisponível
        </div>
      </div>
      <strong class="global-security-alert-camera">
        {{ activeSecurityAlert.camera_name || 'Câmera' }}
      </strong>
      <span class="global-security-alert-meta">
        {{ formatSecurityAlertDate(activeSecurityAlert.detected_at) }}
        <template v-if="activeSecurityAlert.confidence">
          · confiança {{ formatConfidence(activeSecurityAlert.confidence) }}
        </template>
      </span>
      <span class="global-security-alert-action">
        Abrir no mosaico <i class="ph ph-arrow-right"></i>
      </span>
    </aside>
  </transition>
</template>

<script lang="ts">
import { defineComponent, ref, onBeforeUnmount, onMounted } from "vue";
import { mapGetters } from "vuex";
import Pusher from "pusher-js";
import api from "../../config/axios.js";
import LightDarkSwtichBtn from "./LightDarkSwtichBtn.vue";
import stateStore from "../../utils/store";
import LogoutModal from "../Authentication/LogoutModal.vue";

interface SecurityAlert {
  alert_id?: string | number;
  id?: string | number;
  camera_id?: string | number;
  camera_token?: string;
  camera_name?: string | null;
  confidence?: string | number | null;
  event_type?: string;
  detected_at?: string;
  snapshot_available?: boolean;
  snapshot_url?: string | null;
}

type PusherChannel = ReturnType<Pusher["subscribe"]>;
type WebkitAudioWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

let alertAudioContext: AudioContext | null = null;

export default defineComponent({
  name: "MainHeader",
  components: {
    LightDarkSwtichBtn,
    LogoutModal
  },
  data() {
    return {
      recentSecurityAlerts: [] as SecurityAlert[],
      notificationsLoading: false,
      activeSecurityAlert: null as SecurityAlert | null,
      brokenSnapshotIds: {} as Record<string, boolean>,
      securityPusher: null as Pusher | null,
      securityChannel: null as PusherChannel | null,
      currentSecurityChannelName: null as string | null,
      currentSecurityEventName: null as string | null,
      securityAlertTimer: null as number | null,
    };
  },
  computed: {
    ...mapGetters([
      "getConfigurations",
      "getCompany_id"
    ])
  },
  watch: {
    getCompany_id: {
      immediate: true,
      handler(companyId) {
        if (!companyId) return;

        this.loadRecentSecurityAlerts();
        this.setupSecurityNotifications(companyId);
      }
    }
  },
  methods: {
    async loadRecentSecurityAlerts() {
      this.notificationsLoading = true;

      try {
        const response = await api.axios.get("/api/ai-alerts/recent");
        this.recentSecurityAlerts = Array.isArray(response.data?.alerts)
          ? response.data.alerts.map(this.normalizeSecurityAlert)
          : [];
      } catch (error) {
        this.recentSecurityAlerts = [];
      } finally {
        this.notificationsLoading = false;
      }
    },
    setupSecurityNotifications(companyId: string | number) {
      const channelName = `security-alert-${companyId}`;
      if (this.currentSecurityChannelName === channelName && this.securityPusher) return;

      this.stopSecurityNotifications();
      this.securityPusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        cluster: process.env.VUE_APP_PUSHER_CLUSTER,
        forceTLS: true
      });
      this.currentSecurityChannelName = channelName;
      this.currentSecurityEventName = channelName;
      this.securityChannel = this.securityPusher.subscribe(channelName);
      this.securityChannel.bind(channelName, this.handleSecurityNotification);
    },
    stopSecurityNotifications() {
      if (this.securityChannel && this.currentSecurityEventName) {
        this.securityChannel.unbind(this.currentSecurityEventName, this.handleSecurityNotification);
      }
      if (this.securityPusher && this.currentSecurityChannelName) {
        this.securityPusher.unsubscribe(this.currentSecurityChannelName);
        this.securityPusher.disconnect();
      }

      this.securityPusher = null;
      this.securityChannel = null;
      this.currentSecurityChannelName = null;
      this.currentSecurityEventName = null;
    },
    normalizeSecurityAlert(alert: SecurityAlert): SecurityAlert {
      return {
        ...alert,
        alert_id: alert.alert_id || alert.id,
        snapshot_available: alert.snapshot_available !== false && Boolean(alert.snapshot_url)
      };
    },
    handleSecurityNotification(alert: SecurityAlert) {
      if (!alert) return;

      const normalizedAlert = this.normalizeSecurityAlert(alert);
      this.recentSecurityAlerts = [
        normalizedAlert,
        ...this.recentSecurityAlerts.filter(
          (item) => String(item.alert_id) !== String(normalizedAlert.alert_id)
        )
      ].slice(0, 10);

      if (this.$route.path !== "/realtime") {
        this.activeSecurityAlert = normalizedAlert;
        this.clearSecurityAlertTimer();
        this.securityAlertTimer = window.setTimeout(() => {
          this.dismissActiveSecurityAlert();
        }, 15000);
      }

      this.playAlertSound();
    },
    async navigateToSecurityAlert(alert: SecurityAlert) {
      if (!alert?.camera_token) return;

      this.dismissActiveSecurityAlert();
      await this.$router.push({
        path: "/realtime",
        query: {
          camera_token: String(alert.camera_token),
          alert_id: String(alert.alert_id || "")
        }
      });
    },
    dismissActiveSecurityAlert() {
      this.activeSecurityAlert = null;
      this.clearSecurityAlertTimer();
    },
    clearSecurityAlertTimer() {
      if (this.securityAlertTimer !== null) {
        clearTimeout(this.securityAlertTimer);
        this.securityAlertTimer = null;
      }
    },
    hasSnapshot(alert: SecurityAlert | null) {
      if (!alert?.snapshot_url || alert.snapshot_available === false) return false;
      return !this.brokenSnapshotIds[String(alert.alert_id)];
    },
    markSnapshotUnavailable(alert: SecurityAlert) {
      this.brokenSnapshotIds = {
        ...this.brokenSnapshotIds,
        [String(alert.alert_id)]: true
      };
    },
    formatSecurityAlertDate(value?: string) {
      if (!value) return "Agora";

      return new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    },
    formatConfidence(value?: string | number | null) {
      return `${Math.round(Number(value) * 100)}%`;
    },
    getAlertAudioContext() {
      if (!alertAudioContext) {
        const AudioContextConstructor = window.AudioContext
          || (window as WebkitAudioWindow).webkitAudioContext;
        if (!AudioContextConstructor) return null;
        alertAudioContext = new AudioContextConstructor();
      }

      return alertAudioContext;
    },
    async unlockAlertAudio() {
      const context = this.getAlertAudioContext();
      if (!context) return;

      try {
        if (context.state === "suspended") {
          await context.resume();
        }
        if (context.state === "running") {
          window.removeEventListener("pointerdown", this.unlockAlertAudio);
          window.removeEventListener("keydown", this.unlockAlertAudio);
        }
      } catch (error) {
        // O navegador permitirá o áudio após a próxima interação do usuário.
      }
    },
    async playAlertSound() {
      const context = this.getAlertAudioContext();
      if (!context) return;

      try {
        if (context.state === "suspended") {
          await context.resume();
        }
        if (context.state !== "running") return;

        const startAt = context.currentTime;
        [
          { frequency: 880, offset: 0 },
          { frequency: 660, offset: 0.18 },
          { frequency: 880, offset: 0.36 }
        ].forEach(({ frequency, offset }) => {
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          const toneStart = startAt + offset;

          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(frequency, toneStart);
          gain.gain.setValueAtTime(0.0001, toneStart);
          gain.gain.exponentialRampToValueAtTime(0.16, toneStart + 0.018);
          gain.gain.exponentialRampToValueAtTime(0.0001, toneStart + 0.14);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(toneStart);
          oscillator.stop(toneStart + 0.15);
        });
      } catch (error) {
        // Sem interrupção visual caso o navegador bloqueie reprodução automática.
      }
    }
  },
  setup() {
    const stateStoreInstance = stateStore;
    const isSticky = ref(false);
    const handleScroll = () => {
      isSticky.value = window.scrollY >= 100;
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    return {
      isSticky,
      stateStoreInstance,
    };
  },
  mounted() {
    window.addEventListener("pointerdown", this.unlockAlertAudio);
    window.addEventListener("keydown", this.unlockAlertAudio);
  },
  beforeUnmount() {
    this.stopSecurityNotifications();
    this.clearSecurityAlertTimer();
    window.removeEventListener("pointerdown", this.unlockAlertAudio);
    window.removeEventListener("keydown", this.unlockAlertAudio);
  }
});
</script>

<style scoped>
.area-buttons-header {
  display: flex;
  justify-content: flex-end;
}

.custom-mobile {
  width: 100%;
}

.header-right-side {
  gap: 18px;
}

.security-notification-dropdown {
  position: relative;
}

.security-notification-toggle {
  position: relative;
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #3b3b50;
  background: #f5f5fb;
  font-size: 22px;
  transition: 0.2s ease;
}

.security-notification-toggle::after {
  display: none;
}

.security-notification-toggle:hover {
  color: #fff;
  background: #5d5fef;
}

.security-notification-badge {
  position: absolute;
  top: -4px;
  right: -5px;
  display: inline-flex;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  color: #fff;
  background: #dc3545;
  font-size: 10px;
  font-weight: 700;
}

.security-notification-menu {
  right: 0 !important;
  left: auto !important;
  width: min(410px, calc(100vw - 30px)) !important;
  margin-top: 12px !important;
  margin-right: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  box-shadow: 0 18px 45px rgba(32, 32, 58, 0.18);
}

.security-notification-header {
  display: flex;
  min-height: 66px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 17px;
  border-bottom: 1px solid #ececf3;
}

.security-notification-header strong,
.security-notification-header span {
  display: block;
}

.security-notification-header strong {
  color: #242438;
  font-size: 15px;
}

.security-notification-header div > span,
.security-notification-loading {
  color: #7a7a8c;
  font-size: 12px;
}

.security-notification-list {
  max-height: min(480px, calc(100vh - 190px));
  overflow-y: auto;
}

.security-notification-list li + li {
  border-top: 1px solid #f0f0f5;
}

.security-notification-item {
  display: grid;
  width: 100%;
  grid-template-columns: 80px minmax(0, 1fr) 18px;
  gap: 12px;
  align-items: center;
  padding: 12px 15px;
  border: 0;
  color: inherit;
  background: transparent;
  text-align: left;
  transition: background 0.2s ease;
}

.security-notification-item:hover {
  background: #f7f7fc;
}

.security-notification-image {
  display: flex;
  width: 80px;
  height: 58px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 7px;
  color: #8b8b9c;
  background: #ececf3;
  font-size: 24px;
}

.security-notification-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.security-notification-content,
.security-notification-content strong,
.security-notification-content span,
.security-notification-content small {
  display: block;
}

.security-notification-content strong {
  overflow: hidden;
  color: #29293e;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.security-notification-content span {
  margin-top: 3px;
  color: #dc3545;
  font-size: 12px;
  font-weight: 700;
}

.security-notification-content small {
  margin-top: 3px;
  color: #78788a;
  font-size: 11px;
}

.security-notification-arrow {
  color: #9c9cad;
  font-size: 16px;
}

.security-notification-empty {
  display: flex;
  min-height: 150px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #858596;
  font-size: 13px;
}

.security-notification-empty i {
  font-size: 28px;
}

.global-security-alert {
  position: fixed;
  top: 105px;
  right: 28px;
  z-index: 1090;
  width: 330px;
  padding: 16px;
  overflow: hidden;
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(145deg, #24191d, #16161f);
  box-shadow: 0 20px 55px rgba(12, 12, 22, 0.42);
  cursor: pointer;
}

.global-security-alert:focus-visible {
  outline: 3px solid rgba(93, 95, 239, 0.55);
  outline-offset: 3px;
}

.global-security-alert-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
}

.global-security-alert-kicker {
  color: #ff7c87;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.global-security-alert-title {
  margin: 3px 34px 12px 0;
  font-size: 18px;
  font-weight: 800;
}

.global-security-alert-frame {
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 8px;
  background: #09090d;
}

.global-security-alert-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.global-security-alert-frame-empty {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #9d9da8;
  font-size: 12px;
}

.global-security-alert-frame-empty i {
  font-size: 28px;
}

.global-security-alert-camera,
.global-security-alert-meta,
.global-security-alert-action {
  display: block;
}

.global-security-alert-camera {
  margin-top: 11px;
  font-size: 14px;
}

.global-security-alert-meta {
  margin-top: 3px;
  color: #b9b9c4;
  font-size: 11px;
}

.global-security-alert-action {
  margin-top: 11px;
  color: #ff8d97;
  font-size: 12px;
  font-weight: 700;
}

.security-alert-pop-enter-active,
.security-alert-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.security-alert-pop-enter-from,
.security-alert-pop-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}

:global(.dark) .security-notification-toggle {
  color: #dadae5;
  background: #29293b;
}

:global(.dark) .security-notification-menu {
  background: #222233 !important;
}

:global(.dark) .security-notification-header,
:global(.dark) .security-notification-list li + li {
  border-color: #38384b;
}

:global(.dark) .security-notification-header strong,
:global(.dark) .security-notification-content strong {
  color: #f5f5fa;
}

:global(.dark) .security-notification-item:hover {
  background: #2b2b3e;
}

@media (max-width: 767px) {
  .header-area {
    display: none !important;
  }

  .header-burger-menu {
    display: none !important;
  }

  .global-security-alert {
    top: 18px;
    right: 15px;
    left: 15px;
    width: auto;
  }
}
</style>
